import { useEffect, useRef, useState, type ReactElement } from 'react';
import { api } from './api';

const OWNER_EMAIL = 'vjakomin@gmail.com';

type Props = {
    token: string | null;
    userEmail: string | null | undefined;
    locale: 'hr' | 'en';
};

type StatusResp = { state: string; at: string | null; by: string | null; pending: boolean };

/**
 * "Resetiraj sandbox" — vidljiv SAMO vlasniku (vjakomin). Okida trigger-file na produ; root cron
 * watcher pokreće stvarni reset (mysqldump prod→sandbox + rsync koda). Pratimo status pollingom.
 */
export function SandboxResetButton({ token, userEmail, locale }: Props): ReactElement | null {
    const [busy, setBusy] = useState(false);
    const [state, setState] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<number | null>(null);
    const startedRef = useRef<number>(0);

    const en = locale === 'en';

    useEffect(() => () => {
        if (pollRef.current) window.clearInterval(pollRef.current);
    }, []);

    if ((userEmail ?? '').toLowerCase() !== OWNER_EMAIL) {
        return null;
    }

    function stopPolling(): void {
        if (pollRef.current) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }

    async function poll(): Promise<void> {
        try {
            const r = await api<StatusResp>('/admin/sandbox/reset-status', token);
            setState(r.state);
            if (r.state === 'done') {
                stopPolling();
                setBusy(false);
                window.setTimeout(() => setState(null), 6000);
            } else if (r.state === 'failed') {
                stopPolling();
                setBusy(false);
                setError(en ? 'Reset failed — check server log.' : 'Reset nije uspio — provjeri log na serveru.');
            } else if (Date.now() - startedRef.current > 8 * 60 * 1000) {
                stopPolling();
                setBusy(false);
                setError(en ? 'Taking longer than expected — check server.' : 'Traje duže nego očekivano — provjeri server.');
            }
        } catch {
            // prolazni error u pollingu — ignoriraj, idući tick
        }
    }

    async function onClick(): Promise<void> {
        setError(null);
        const ok = window.confirm(en
            ? 'This overwrites the ENTIRE sandbox with a fresh 1:1 copy of production (DB + code). Trainees\' practice work will be lost. Continue?'
            : 'Ovo prebrisuje CIJELI sandbox svježom 1:1 kopijom produkcije (baza + kod). Vježbe polaznika će se izgubiti. Nastaviti?');
        if (!ok) return;
        setBusy(true);
        setState('queued');
        startedRef.current = Date.now();
        try {
            await api('/admin/sandbox/reset', token, { method: 'POST' });
            stopPolling();
            pollRef.current = window.setInterval(() => { void poll(); }, 4000);
            void poll();
        } catch (e) {
            setBusy(false);
            setState(null);
            setError(e instanceof Error ? e.message : (en ? 'Could not start reset.' : 'Ne mogu pokrenuti reset.'));
        }
    }

    const label = busy
        ? (state === 'running'
            ? (en ? 'Resetting…' : 'Resetiram…')
            : (en ? 'Queued…' : 'U redu čekanja…'))
        : (en ? 'Reset sandbox' : 'Resetiraj sandbox');

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 8 }}>
            <button
                type="button"
                onClick={() => { void onClick(); }}
                disabled={busy}
                title={en ? 'Owner only — rebuilds sandbox from production' : 'Samo vlasnik — sandbox se gradi iz produkcije'}
                style={{
                    fontSize: '0.74rem', color: '#fff', textDecoration: 'none',
                    border: '1px solid #86198f', borderRadius: 6, padding: '3px 9px',
                    background: busy ? '#86198f' : 'linear-gradient(90deg,#c026d3,#86198f)',
                    fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.85 : 1,
                }}>
                ♻️ {label}
            </button>
            {state === 'done' ? <span style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700 }}>{en ? 'Done ✓' : 'Gotovo ✓'}</span> : null}
            {error ? <span style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 700 }}>{error}</span> : null}
        </span>
    );
}
